import { supabase } from '../config/supabase';

export interface CourseLeadData {
  // Dados pessoais
  name: string;
  email: string;
  phone: string;
  profession: string;
  experience: string;
  
  // Dados do interesse
  selectedCourse?: string;
  selectedCourseTitle?: string;
  interests: string[];
  message?: string;
  
  // Dados de controle
  source: 'website' | 'whatsapp' | 'email' | 'phone';
  status: 'new' | 'contacted' | 'interested' | 'enrolled' | 'not_interested' | 'resolved';
  createdAt: string;
  updatedAt: string;
  
  // Dados de acompanhamento
  contactAttempts?: number;
  lastContactDate?: string;
  
  // Dados de conversão
  convertedToCourse?: boolean;
  enrolledCourseId?: string;
  enrollmentDate?: string;
  notes?: string;
}

export interface PublicCourseLeadPayload {
  tenantSlug: string;
  courseId: string;
  name: string;
  email: string;
  phone?: string;
  profession?: string;
  experience?: string;
  interests?: string[];
  message?: string;
}

// Função de mapeamento para evitar repetição
const mapLeadData = (lead: any): CourseLeadData & { id: string } => ({
  id: lead.courseLeadsId,
  name: lead.courseLeadsName,
  email: lead.courseLeadsEmail,
  phone: lead.courseLeadsPhone,
  profession: lead.courseLeadsProfession,
  experience: lead.courseLeadsExperience,
  selectedCourse: lead.courseLeadsSelectedCourse,
  selectedCourseTitle: lead.courseLeadsSelectedCourseTitle,
  interests: lead.courseLeadsInterests,
  message: lead.courseLeadsMessage,
  source: lead.courseLeadsSource,
  status: lead.courseLeadsStatus,
  createdAt: lead.courseLeadsCreatedAt,
  updatedAt: lead.courseLeadsUpdatedAt,
  contactAttempts: lead.courseLeadsContactAttempts,
  lastContactDate: lead.courseLeadsLastContactDate,
  convertedToCourse: lead.courseLeadsConvertedToCourse,
  enrolledCourseId: lead.courseLeadsEnrolledCourseId,
  enrollmentDate: lead.courseLeadsEnrollmentDate,
  notes: lead.courseLeadsNotes
});

export const createCourseLead = async (leadData: Omit<CourseLeadData, 'createdAt' | 'updatedAt' | 'status' | 'source'> & { clientId: string }): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('courseleads')
      .insert({
        "clientId": leadData.clientId,
        "courseLeadsName": leadData.name,
        "courseLeadsEmail": leadData.email,
        "courseLeadsPhone": leadData.phone,
        "courseLeadsProfession": leadData.profession,
        "courseLeadsExperience": leadData.experience,
        "courseLeadsSelectedCourse": leadData.selectedCourse || null,
        "courseLeadsSelectedCourseTitle": leadData.selectedCourseTitle || null,
        "courseLeadsInterests": leadData.interests || [],
        "courseLeadsMessage": leadData.message || null,
        "courseLeadsSource": 'website',
        "courseLeadsStatus": 'new'
      })
      .select();

    if (error) {
      throw error;
    }

    if (data && data.length > 0) {
      console.log('Lead criado com ID:', data[0].courseLeadsId);
      return data[0].courseLeadsId;
    } else {
      throw new Error('Falha ao criar lead no Supabase.');
    }
  } catch (error) {
    console.error('Erro ao criar lead:', error);
    throw error;
  }
};

export const createPublicCourseLead = async (payload: PublicCourseLeadPayload): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc("submit_public_course_lead", {
      p_slug: payload.tenantSlug,
      p_course_id: payload.courseId,
      p_name: payload.name,
      p_email: payload.email,
      p_phone: payload.phone ?? null,
      p_profession: payload.profession ?? null,
      p_experience: payload.experience ?? null,
      p_interests: payload.interests ?? [],
      p_message: payload.message ?? null,
    });

    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error("Falha ao criar lead público.");
    }

    return data as string;
  } catch (error) {
    console.error("Erro ao criar lead público:", error);
    throw error;
  }
};

export const deleteLead = async (leadId: string, clientId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('courseleads')
      .delete()
      .eq('courseLeadsId', leadId)
      .eq('clientId', clientId);

    if (error) {
      throw error;
    }
    console.log('Lead excluído com ID:', leadId);
  } catch (error) {
    console.error('Erro ao excluir lead:', error);
    throw error;
  }
};

export const updateLeadStatus = async (
  leadId: string,
  status: CourseLeadData["status"],
  notes?: string,
  clientId?: string
): Promise<void> => {
  try {
    if (!clientId) {
      throw new Error("clientId é obrigatório para atualizar status do lead.");
    }

    const { data: currentLead, error: fetchError } = await supabase
      .from('courseleads')
      .select('courseLeadsContactAttempts')
      .eq('courseLeadsId', leadId)
      .eq('clientId', clientId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    let updateData: any = {
      courseLeadsStatus: status,
      courseLeadsUpdatedAt: new Date().toISOString(),
    };

    if (notes) {
      updateData.courseLeadsNotes = notes;
    }

    if (status === 'contacted') {
      updateData.courseLeadsContactAttempts = (currentLead?.courseLeadsContactAttempts || 0) + 1;
      updateData.courseLeadsLastContactDate = new Date().toISOString();
    }

    if (status === 'enrolled') {
      updateData.courseLeadsConvertedToCourse = true;
      updateData.courseLeadsEnrollmentDate = new Date().toISOString();
    }

    const { error } = await supabase
      .from('courseleads')
      .update(updateData)
      .eq('courseLeadsId', leadId)
      .eq('clientId', clientId);

    if (error) {
      throw error;
    }
    console.log('Status do lead atualizado:', leadId, status);
  } catch (error) {
    console.error('Erro ao atualizar status do lead:', error);
    throw error;
  }
};

export const updateMultipleLeadsStatus = async (
  leadIds: string[],
  status: CourseLeadData['status'],
  notes?: string,
  clientId?: string
): Promise<void> => {
  try {
    if (!clientId) {
      throw new Error("clientId é obrigatório para atualizar leads em lote.");
    }

    const promises = leadIds.map(leadId => updateLeadStatus(leadId, status, notes, clientId));
    await Promise.all(promises);
    console.log(`${leadIds.length} leads atualizados para status: ${status}`);
  } catch (error) {
    console.error('Erro ao atualizar múltiplos leads:', error);
    throw error;
  }
};

export const addLeadNote = async (leadId: string, note: string, clientId: string): Promise<void> => {
  try {
    const { data: currentLead, error: fetchError } = await supabase
      .from('courseleads')
      .select('courseLeadsNotes')
      .eq('courseLeadsId', leadId)
      .eq('clientId', clientId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    const currentNotes = currentLead?.courseLeadsNotes || '';
    const timestamp = new Date().toLocaleString('pt-BR');
    const newNote = `[${timestamp}] ${note}`;
    const updatedNotes = currentNotes ? `${currentNotes}\n${newNote}` : newNote;

    const { error } = await supabase
      .from('courseleads')
      .update({
        courseLeadsNotes: updatedNotes,
        courseLeadsUpdatedAt: new Date().toISOString()
      })
      .eq('courseLeadsId', leadId)
      .eq('clientId', clientId);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Erro ao adicionar nota:', error);
    throw error;
  }
};

export const getAllLeads = async (clientId?: string): Promise<(CourseLeadData & { id: string })[]> => {
  try {
    if (!clientId) {
      return [];
    }

    const { data, error } = await supabase
      .from("courseleads")
      .select("*")
      .eq("clientId", clientId)
      .order("courseLeadsCreatedAt", { ascending: false });

    if (error) {
      throw error;
    }

    return data.map(mapLeadData);
  } catch (error) {
    console.error("Erro ao buscar leads:", error);
    throw error;
  }
};

export const getLeadsByStatus = async (status: CourseLeadData["status"], clientId?: string): Promise<(CourseLeadData & { id: string })[]> => {
  try {
    if (!clientId) {
      return [];
    }

    const { data, error } = await supabase
      .from("courseleads")
      .select("*")
      .eq("clientId", clientId)
      .eq("courseLeadsStatus", status)
      .order("courseLeadsCreatedAt", { ascending: false });

    if (error) {
      throw error;
    }

    return data.map(mapLeadData);
  } catch (error) {
    console.error("Erro ao buscar leads por status:", error);
    throw error;
  }
};

export const getLeadsByCourse = async (courseId: string, clientId?: string): Promise<(CourseLeadData & { id: string })[]> => {
  try {
    if (!clientId) {
      return [];
    }

    const { data, error } = await supabase
      .from("courseleads")
      .select("*")
      .eq("clientId", clientId)
      .eq("courseLeadsSelectedCourse", courseId)
      .order("courseLeadsCreatedAt", { ascending: false });

    if (error) {
      throw error;
    }

    return data.map(mapLeadData);
  } catch (error) {
    console.error("Erro ao buscar leads por curso:", error);
    throw error;
  }
};

export const getLeadsStats = async (clientId?: string) => {
  try {
    if (!clientId) {
      return {
        total: 0,
        new: 0,
        contacted: 0,
        interested: 0,
        enrolled: 0,
        notInterested: 0,
        resolved: 0,
        courseStats: {},
        professionStats: {},
        conversionRate: 0
      };
    }

    const { data: leads, error } = await supabase
      .from("courseleads")
      .select("*")
      .eq("clientId", clientId);

    if (error) {
      throw error;
    }
    
    const stats = {
      total: leads.length,
      new: leads.filter(lead => lead.courseLeadsStatus === 'new').length,
      contacted: leads.filter(lead => lead.courseLeadsStatus === 'contacted').length,
      interested: leads.filter(lead => lead.courseLeadsStatus === 'interested').length,
      enrolled: leads.filter(lead => lead.courseLeadsStatus === 'enrolled').length,
      notInterested: leads.filter(lead => lead.courseLeadsStatus === 'not_interested').length,
      resolved: leads.filter(lead => lead.courseLeadsStatus === 'resolved').length,
      
      courseStats: leads.reduce((acc, lead) => {
        if (lead.courseLeadsSelectedCourse) {
          acc[lead.courseLeadsSelectedCourse] = (acc[lead.courseLeadsSelectedCourse] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>),
      
      professionStats: leads.reduce((acc, lead) => {
        if (lead.courseLeadsProfession) { // Check if profession exists
          acc[lead.courseLeadsProfession] = (acc[lead.courseLeadsProfession] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>),
      
      conversionRate: leads.length > 0 ? (leads.filter(lead => lead.courseLeadsConvertedToCourse).length / leads.length) * 100 : 0
    };
    
    return stats;
  } catch (error) {
    console.error('Erro ao calcular estatísticas:', error);
    throw error;
  }
};

export const exportLeadsToCSV = (leads: (CourseLeadData & { id: string })[]): string => {
  const headers = [
    'ID',
    'Nome',
    'Email',
    'Telefone',
    'Profissão',
    'Experiência',
    'Curso Selecionado',
    'Áreas de Interesse',
    'Status',
    'Data de Criação',
    'Última Atualização',
    'Tentativas de Contato',
    'Convertido',
    'Mensagem'
  ];
  
  const csvContent = [
    headers.join(','),
    ...leads.map(lead => [
      lead.id,
      `"${lead.name}"`,
      lead.email,
      lead.phone,
      `"${lead.profession}"`,
      `"${lead.experience}"`,
      `"${lead.selectedCourseTitle || lead.selectedCourse || ''}"`,
      `"${lead.interests.join('; ')}"`,
      lead.status,
      new Date(lead.createdAt).toLocaleDateString('pt-BR'),
      new Date(lead.updatedAt).toLocaleDateString('pt-BR'),
      lead.contactAttempts || 0,
      lead.convertedToCourse ? 'Sim' : 'Não',
      `"${lead.message || ''}"`
    ].join(','))
  ].join('\n');
  
  return csvContent;
};
